import React, { useState, useEffect } from "react";
import "./DealerLogin.css";
import { useNavigate } from "react-router-dom";
import apiService from "../services/apiService";
import analyticsService from "../services/analyticsService";

const DealerLogin = () => {
  const [formData, setFormData] = useState({
    regNumber: "",
    otp: "",
    role: "orguserdealer",
    organization: "trustauto",
  });
  const [step, setStep] = useState(2); // Start directly at step 2: Enter OTP
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const sendOTP = async (regNumber) => {
    setError("");
    setLoading(true);

    try {
      const apiUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      // REMOVED: sendOTPDealer endpoint - replaced by new OTP module
      // const response = await fetch(
      //   `${apiUrl}/trustautodev-7ff75/asia-south1/sendOTPDealer`,
      const response = await apiService.sendOTP({ role: formData.role,
        organization: formData.organization,
        phoneNumber: regNumber,});

      //   if (!response.ok) {
      //     throw new Error(`OTP send failed: ${response.statusText}`);
      //   }

      const result = await response.json();
      console.log("OTP sent successfully:", result);
      setOtpSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    if (e) {
      e.preventDefault();
    }
    if (!formData.regNumber) {
      setError("Registration number is required.");
      return;
    }
    await sendOTP(formData.regNumber);
  };

  const handleResendOTP = async () => {
    setFormData((prev) => ({ ...prev, otp: "" }));
    if (!formData.regNumber) {
      setError("Registration number is required.");
      return;
    }
    await sendOTP(formData.regNumber);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Step 1: Dealer OTP Login
      const datalogin = { ...formData }; // Remove redundant otp assignment
      const dealerResponse = await apiService.loginDealer(datalogin);

      // Improved error check (assuming axios-like response)
      if (!dealerResponse || dealerResponse.status >= 400) {
        const errorMsg =
          dealerResponse?.data?.error?.message ||
          dealerResponse?.statusText ||
          "Dealer login failed. Please verify your OTP.";
        throw new Error(errorMsg);
      }

      const dealerResult = dealerResponse.data; // Use .data directly for axios; adjust if fetch
      console.log("Dealer Login successful:", dealerResult);

      localStorage.setItem("tempRegNumber", formData.regNumber);
      //   alert('Dealer verification successful! Proceeding to login...');

      // Reset form state
      setFormData({
        regNumber: formData.regNumber,
        otp: "",
        role: "orguserdealer",
        organization: "trustauto",
      });
      setStep(2);
      setOtpSent(false);

      // Step 2: Standard User Login (only if temp credentials exist)
      const emailtem = localStorage.getItem("tempdataone") || "";
      const passwordtem = localStorage.getItem("tempdatatwo") || "";

      if (!emailtem || !passwordtem) {
        throw new Error(
          "Missing temporary credentials. Please complete registration first."
        );
      }

      const loginPayload = { email: emailtem, password: passwordtem };
      const loginResponse = await apiService.login(loginPayload);

      // Error check for standard login
      if (!loginResponse || loginResponse.status >= 400) {
        const errorMsg =
          loginResponse?.data?.error?.message ||
          loginResponse?.statusText ||
          "User login failed. Please check your credentials.";
        throw new Error(errorMsg);
      }

      const { token, user } = loginResponse.data;
      console.log("🔍 Login - Full user data from API:", user);

      // Store session data
      localStorage.setItem("authToken", token);
      localStorage.setItem("uid", user.uid);
      localStorage.setItem("email", user.email);
      localStorage.setItem("orgId", user.organization);

      if (user.name) {
        localStorage.setItem("userName", user.name);
        console.log("🔍 Login - Stored userName:", user.name);
      }
      if (user.profileImageUrl) {
        localStorage.setItem("profileImageUrl", user.profileImageUrl);
      }

      // Initialize analytics service with user data
      analyticsService.setUser({
        uid: user.uid,
        email: user.email,
        displayName: user.name || user.email,
        roles: user.roles || ['orguserdealer'],
        organization: user.organization,
        dealerId: user.uid // Use uid as dealerId for dealers
      });

      if (user.profileImageUrl) {
        console.log("🔍 Login - Stored profileImageUrl:", user.profileImageUrl);
      }

      // Log stored data for verification
      console.log("🔍 Login - localStorage after storing:", {
        authToken: localStorage.getItem("authToken") ? "Present" : "Missing",
        uid: localStorage.getItem("uid"),
        email: localStorage.getItem("email"),
        orgId: localStorage.getItem("orgId"),
        userName: localStorage.getItem("userName"),
        profileImageUrl: localStorage.getItem("profileImageUrl"),
      });

      // Dispatch update event
      window.dispatchEvent(new CustomEvent("userDataUpdated"));

      // Update tempRegNumber with mobile from response
      localStorage.setItem("tempRegNumber", user.mobile || formData.regNumber);

      // Navigate based on onboarding status
      console.log(loginResponse.data, "onboarding status");
      localStorage.removeItem("tempdataone");
      localStorage.removeItem("tempdatatwo");

      switch (
        loginResponse.data.onboarding // Use user.onboarding assuming it's in the user object
      ) {
        case "dealer_info":
          localStorage.setItem("onboardingStatus", "/dealer/details");
          navigate("/dealer/details");

          break;
        case "subscription":
          localStorage.setItem("onboardingStatus", "/dealer/subscription");
          navigate("/dealer/subscription");
          break;

        case "upload_docs":
          localStorage.setItem("onboardingStatus", "/dealer/upload");
          navigate("/dealer/upload");
          break;

        case "contract":
          localStorage.setItem("onboardingStatus", "/dealer/contract");
          navigate("/dealer/contract");
          break;

          
        default:
          navigate("/dealerHome");
      }
    } catch (err) {
      console.error("Login error:", err);
      const errorMessage = err.message.includes("Network Error")
        ? "Unable to connect to the server. Please check your network."
        : err.message || "An unexpected error occurred. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const tempRegNumber = localStorage.getItem("tempRegNumber");
    if (tempRegNumber) {
      setFormData((prev) => ({ ...prev, regNumber: tempRegNumber }));
      // Directly send OTP using the retrieved value to avoid state update timing issues
      sendOTP(tempRegNumber);
    } else {
      setError("No registration number found. Please register first.");
      navigate("/dealer/register", { replace: true });
    }
  }, []);

  return (
    <div className="dealer-login-main-container">
      <div className="dealer-login-image-section">
        <div className="dealer-login-image-overlay"></div>
        <div className="dealer-login-image-text">
          Cars
          <span>in Auction Today</span>
        </div>
      </div>

      <div className="dealer-login-form-section">
        <form className="dealer-login-form" onSubmit={handleLogin}>
          {step === 2 && (
            <>
              <div className="dealer-form-group">
                <label htmlFor="otp">Enter OTP</label>
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  value={formData.otp}
                  onChange={handleChange}
                  required
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                />
              </div>

              <button
                type="button"
                onClick={handleResendOTP}
                disabled={loading}
                className="dealer-resend-otp-button"
              >
                {loading ? "Sending..." : "Resend OTP"}
              </button>

              <button
                type="submit"
                disabled={loading || formData.otp.length !== 6}
                className="dealer-submit-button"
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </>
          )}

          {error && <div className="dealer-error-message">{error}</div>}
        </form>
      </div>
    </div>
  );
};

export default DealerLogin;
